import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { clamp } from 'utils/calc';
import { HapticsImpactStyle, hapticImpact } from 'utils/haptic';

import Button from './button';
import DisplayValue from './display-value';
import { messages } from './messages';
import MinusIcon from './minus-icon';
import PlusIcon from './plus-icon';
import Value from './value';

const Container = styled.div`
  display: flex;
  flex: 1;
`;

const Display = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  font-size: 0.9375rem;
  font-family: ${Styles.fontFamily.body};
`;

const Controller = styled.div`
  display: flex;
  flex: 1;
  align-items: flex-end;
  font-size: 0.9375rem;
  font-family: ${Styles.fontFamily.body};
  font-weight: 500;
  justify-content: flex-end;
`;

interface IDisabled {
  disabled?: boolean;
}

export interface IIncrementor {
  children?: React.ReactNode;
  value: number;
  valueDisplayTransformer?: (v: number) => number | string;
  onChange(v: number): void;
  border?: boolean;
  'data-testId'?: string | null;
  'aria-label': string;
  min?: number;
  max?: number;
  name?: string;
  valueWidth?: string;
  IncrementButton?: <T extends IDisabled>(props: T) => JSX.Element;
  DecrementButton?: <T extends IDisabled>(props: T) => JSX.Element;
}

const returnsString = (
  fn: Exclude<IIncrementor['valueDisplayTransformer'], undefined>,
  testValue: number
): fn is (v: number) => string => typeof fn(testValue) === 'string';

export default ({
  name,
  children,
  value = 0,
  valueDisplayTransformer = v => v,
  onChange,
  border = false,
  'data-testId': dataTestId,
  'aria-label': ariaLabel,
  min = Number.NEGATIVE_INFINITY,
  max = Number.POSITIVE_INFINITY,
  valueWidth,
  DecrementButton = MinusIcon,
  IncrementButton = PlusIcon
}: IIncrementor) => {
  const clampV = useMemo(() => clamp(min, max), [min, max]);
  const decrementDisabled = value === min;
  const incrementDisabled = value === max;
  const { formatMessage } = useIntl();

  const [decrementAriaLabel, incrementAriaLabel] = useMemo(() => {
    if (returnsString(valueDisplayTransformer, value)) {
      return [
        decrementDisabled ? valueDisplayTransformer(min) : valueDisplayTransformer(value - 1),
        incrementDisabled ? valueDisplayTransformer(max) : valueDisplayTransformer(value + 1)
      ];
    }
    return [
      formatMessage(messages.removeOneName, { name }),
      formatMessage(messages.addOneName, { name })
    ];
  }, [
    incrementDisabled,
    decrementDisabled,
    max,
    min,
    value,
    valueDisplayTransformer,
    formatMessage,
    name
  ]);

  const valueId = useMemo(() => `item-quantity-${name?.replace(/\s+/g, '-')}`, [name]);

  return (
    <Container data-testid="mutate-quantity" aria-label={ariaLabel}>
      {children && <Display>{children}</Display>}
      <Controller>
        <Button
          data-testid="decrement"
          onClick={e => {
            hapticImpact({ style: HapticsImpactStyle.Light });

            e.preventDefault();
            onChange(clampV(value - 1));
          }}
          disabled={decrementDisabled}
          aria-label={decrementAriaLabel}
        >
          <DecrementButton disabled={decrementDisabled} />
        </Button>
        <Value id={valueId} width={valueWidth} data-testid={dataTestId} border={border}>
          <DisplayValue value={valueDisplayTransformer(value)} />
        </Value>
        <Button
          data-testid="increment"
          onClick={e => {
            hapticImpact({ style: HapticsImpactStyle.Light });

            e.preventDefault();
            onChange(clampV(value + 1));
          }}
          disabled={incrementDisabled}
          aria-label={incrementAriaLabel}
        >
          <IncrementButton disabled={incrementDisabled} />
        </Button>
      </Controller>
    </Container>
  );
};
